import { inject, Injectable } from '@angular/core';

import { getWorkflowNodeBase } from '@blaze-runtime/workflow-domain';
import { type ConnectorInfo } from '@blaze-shared/api-contracts-domain';
import { type DataBindingSourceInfo } from '@blaze-shared/elements-domain';
import { API_BASE_URL } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { deepClone, regexpGetOnlyLettersAndNumbers } from '@blaze-shared/shared-util';
import {
  type ApiCallWorkflowNode,
  type DelayWorkflowNode,
  type ExecuteJavascriptWorkflowNode,
  getInput1DisplayInfo,
  isExecuteJavascriptNode,
  isGetNode,
  type NavigateWorkflowNode,
  type Workflow,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { RuntimeFacade } from '../../+state';
import { DEFAULT_SUCCESS_MESSAGE } from '../../elements/form-container/form-container.model';

/** The line items a checkout is built from, in the shape the connector's API expects. */
interface CheckoutItems {
  displayInfosObject: Record<string, unknown>;
  value: unknown[];
}

/**
 * Builds the workflow that takes a cart to a payment provider's checkout.
 *
 * Each provider needs a different shape of workflow — Foxy drives its own cart from the page,
 * NMI and the hosted-link providers hand off to a checkout URL — so the node list is generated per
 * provider rather than configured. Regenerating an existing payment workflow rewrites only the
 * checkout node, so that anything the author added around it survives.
 */
@Injectable({ providedIn: 'root' })
export class PaymentWorkflowNodesService {
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);

  /**
   * @returns the payment workflow's nodes, or none when the element has no `Get`-backed source to
   * read the cart from.
   * @throws when the element's data source has no schema, which the reference column comes from.
   */
  getPaymentNodes(
    elementDataSource: DataBindingSourceInfo,
    connector: ConnectorInfo | undefined,
    currentPaymentWf?: Workflow,
    dataSourceWf?: Workflow,
    successWfId?: string,
    catalogColumn = '',
    quantityColumn = '',
    referenceColumn = '',
    publishUrl = '',
  ): WorkflowNode[] {
    if (!elementDataSource || !dataSourceWf?.nodes?.length || !isGetNode(dataSourceWf.nodes[0])) {
      return [];
    }
    const sourceSchema = this.schemaService.getSchema(
      elementDataSource.id,
      elementDataSource.sourceType === 'context',
    );
    if (!sourceSchema) {
      throw new Error('Could not find workflow output schema');
    }

    const idColumn =
      sourceSchema.columns.find((c) => c.isId)?.name ||
      elementDataSource.columns.find((c) => c.isId)?.name ||
      '';
    if (!idColumn) {
      console.warn('Id column not found. The workflow query needs to be updated.');
    }

    if (connector?.target === 'NMI') {
      return this.createPaymentNodesForNMI(
        dataSourceWf,
        successWfId,
        referenceColumn,
        publishUrl,
        currentPaymentWf,
      );
    }
    if (connector?.target === 'Foxy') {
      return this.createPaymentNodesForFoxy(
        connector,
        dataSourceWf,
        successWfId,
        referenceColumn,
        currentPaymentWf,
      );
    }
    return this.createPaymentNodesForSquareAndStripe(
      connector,
      dataSourceWf,
      successWfId,
      referenceColumn,
      this.checkoutItemsFor(connector, catalogColumn, quantityColumn),
      currentPaymentWf,
    );
  }

  /** Square and Stripe name the catalogued product differently, but carry the same two fields. */
  private checkoutItemsFor(
    connector: ConnectorInfo | undefined,
    catalogColumn: string,
    quantityColumn: string,
  ): CheckoutItems {
    const catalogField =
      connector?.target === 'Square'
        ? 'CatalogId'
        : connector?.target === 'Stripe'
          ? 'PriceId'
          : undefined;
    if (!catalogField) {
      return { displayInfosObject: {}, value: [] };
    }
    return {
      value: [
        {
          [catalogField]: `$$input1["${catalogColumn}"]`,
          Quantity: `$$input1["${quantityColumn}"]`,
        },
      ],
      displayInfosObject: {
        0: {
          [catalogField]: [getInput1DisplayInfo(catalogColumn)],
          Quantity: [getInput1DisplayInfo(quantityColumn)],
        },
      },
    };
  }

  /**
   * Foxy has no checkout API: the cart is filled item by item from the page, then the user is sent
   * to the store's own checkout. The delay is there so the cart is up to date before they arrive.
   */
  private createPaymentNodesForFoxy(
    connector: ConnectorInfo | undefined,
    dataSourceWf: Workflow,
    successWfId: string | undefined,
    referenceColumn = '',
    currentPaymentWf?: Workflow,
  ): WorkflowNode[] {
    const viewCartUrl = `https://${connector?.configuration?.storeSubDomain}.foxycart.com/checkout`;
    console.log(`
      Payment workflow generated.
      View Cart URL: ${viewCartUrl}
      Success URL: ${this.successUrl(successWfId, referenceColumn)}
    `);
    const checkoutItemsJs =
      '' +
      '\n// Clear before adding items' +
      '\nFC.client.request(`https://${FC.settings.storedomain}/cart?cart=empty`).then(() => {' +
      '\n  // Add items sequentially' +
      '\n  function addToCart(itemIndex) {' +
      '\n    const item = $$input1[itemIndex];' +
      '\n    if (!item) { return; }' +
      '\n    FC.client.request(`https://${FC.settings.storedomain}/cart?name=${item["sku"]}&quantity=${item["quantity"]}&price=${item["price"]}&coupon=${item["discountCode"]}`, { "h:externalAccountRowId": $$Account["_id"], "h:cartId": item["cartId"] }).then(() => addToCart(itemIndex + 1));' +
      '\n  }' +
      '\n  addToCart(0);' +
      '\n});';

    const checkoutNode = {
      id: 'payment_executejs_checkout',
      nodeType: 'ExecuteJavascript',
      nextNodeId: 'payment_delay',
      value: checkoutItemsJs,
    } as ExecuteJavascriptWorkflowNode;

    const nodes: WorkflowNode[] = [
      { ...dataSourceWf.nodes[0], id: 'payment_get_lineitems', nextNodeId: checkoutNode.id },
      checkoutNode,
      {
        id: 'payment_delay',
        nodeType: 'Delay',
        delayMs: 3000,
        nextNodeId: 'payment_open_checkout_page',
      } as DelayWorkflowNode,
      {
        id: 'payment_open_checkout_page',
        navigateType: 'Website',
        nextNodeId: null,
        nodeType: 'Navigate',
        openInNewTab: true,
        target: viewCartUrl,
      } as NavigateWorkflowNode,
    ];

    return this.rewriteScriptCheckoutNode(nodes, checkoutNode, currentPaymentWf);
  }

  /**
   * NMI redirects to a hosted checkout, which only accepts `sku` and `quantity` per line item and
   * insists on valid success and cancel URLs (BL-5635).
   */
  private createPaymentNodesForNMI(
    dataSourceWf: Workflow,
    successWfId: string | undefined,
    referenceColumn = '',
    publishUrl: string,
    currentPaymentWf?: Workflow,
  ): WorkflowNode[] {
    const checkoutPayload = `{
  lineItems: $$input1.filter((item) => !!item).map((item) => ({
    sku: item.sku,
    quantity: item.quantity,
  })),
    successUrl: ${this.successUrl(successWfId, referenceColumn)},
  cancelUrl: "${publishUrl || 'PLEASE PUBLISH PROJECT AND PASTE THE URL HERE'}",
    } `.replaceAll('   ', ' ');

    const checkoutNode = {
      id: 'payment_executejs_checkout',
      nodeType: 'ExecuteJavascript',
      nextNodeId: null,
      value: `CollectCheckout.redirectToCheckout(${checkoutPayload})`,
    } as ExecuteJavascriptWorkflowNode;

    const nodes: WorkflowNode[] = [
      { ...dataSourceWf.nodes[0], id: 'payment_get_lineitems', nextNodeId: checkoutNode.id },
      checkoutNode,
    ];

    return this.rewriteScriptCheckoutNode(nodes, checkoutNode, currentPaymentWf);
  }

  /** Square and Stripe both mint a payment link through their connector, then navigate to it. */
  private createPaymentNodesForSquareAndStripe(
    connector: ConnectorInfo | undefined,
    dataSourceWf: Workflow,
    successWfId: string | undefined,
    referenceColumn: string,
    items: CheckoutItems,
    currentPaymentWf?: Workflow,
  ): WorkflowNode[] {
    const refIdParam = regexpGetOnlyLettersAndNumbers(
      this.schemaService.replaceColumnIdsWithNames(referenceColumn),
    );
    const checkoutNode = {
      id: 'payment_apicall_createpaymentlink',
      nodeType: 'ApiCall',
      nextNodeId: 'payment_navigate_to_payment',
      actionName: 'CreatePaymentLink',
      connectorId: connector?.id,
      parameters: {},
      runOncePerInput: true,
      assignments: [
        {
          name: 'redirectUrl',
          value: `CONCATENATE("${this.redirectBaseUrl()}/workflows/${successWfId}/checkout/redirect?externalAccountRowId=", $$Account["_id"], "&${refIdParam}=", $$input1["${referenceColumn}"])`,
          displayInfos: [],
          displayInfosObject: {},
          isFormulaValue: true,
          isOptional: true,
        },
        {
          name: 'items',
          value: items.value,
          displayInfosObject: items.displayInfosObject,
          isOptional: true,
        },
      ],
      successMessage: DEFAULT_SUCCESS_MESSAGE,
    } as ApiCallWorkflowNode;

    if (connector?.target === 'Square') {
      checkoutNode.assignments?.push({
        name: 'buyerEmail',
        value: '$$User["Email"]',
        displayInfos: [
          {
            display: 'Current User | Email',
            formulaName: '',
            isAutoCompleted: true,
            value: '$$User["Email"]',
          },
        ],
        displayInfosObject: {},
        isFormulaValue: false,
        isOptional: true,
      });
    }

    const nodes: WorkflowNode[] = [
      { ...dataSourceWf.nodes[0], id: 'payment_get_lineitems', nextNodeId: checkoutNode.id },
      checkoutNode,
      {
        ...getWorkflowNodeBase('Navigate', 'payment_navigate_to_payment'),
        navigateType: 'Website',
        target: '$$input1["url"]',
        displayInfos: [
          {
            display: 'url',
            formulaName: '',
            isAutoCompleted: true,
            value: '$$input1["url"]',
          },
        ],
        openInNewTab: false,
        passDataAsInput: false,
      } as NavigateWorkflowNode,
    ];

    const checkoutNodeIndex = this.findCheckoutNodeIndex(currentPaymentWf, checkoutNode.id);
    if (checkoutNodeIndex < 0) {
      return nodes;
    }
    // Keep the existing node's connections, taking only the freshly generated assignments.
    const currentNodes = deepClone(currentPaymentWf?.nodes ?? []);
    currentNodes[checkoutNodeIndex] = {
      ...(currentNodes[checkoutNodeIndex] as ApiCallWorkflowNode),
      assignments: checkoutNode.assignments,
    };
    return currentNodes;
  }

  /**
   * Keeps an already-generated payment workflow as it is, refreshing only the checkout script —
   * the author may have built on top of the generated nodes.
   */
  private rewriteScriptCheckoutNode(
    nodes: WorkflowNode[],
    checkoutNode: ExecuteJavascriptWorkflowNode,
    currentPaymentWf?: Workflow,
  ): WorkflowNode[] {
    const checkoutNodeIndex = this.findCheckoutNodeIndex(currentPaymentWf, checkoutNode.id);
    if (checkoutNodeIndex < 0) {
      return nodes;
    }
    const currentNodes = deepClone(currentPaymentWf?.nodes ?? []);
    const currentCheckoutNode = currentNodes[checkoutNodeIndex];
    if (!isExecuteJavascriptNode(currentCheckoutNode)) {
      throw new Error('Unexpected node type');
    }
    currentCheckoutNode.value = checkoutNode.value;
    currentNodes[checkoutNodeIndex] = currentCheckoutNode;
    return currentNodes;
  }

  /**
   * @returns the index of the existing checkout node, or `-1` when there is no workflow to keep.
   *
   * `|| -1` also rejects index 0, so a workflow whose checkout node happens to come first is
   * regenerated whole rather than patched. Kept as-is: published apps may depend on it.
   */
  private findCheckoutNodeIndex(
    currentPaymentWf: Workflow | undefined,
    checkoutNodeId: string,
  ): number {
    const index = currentPaymentWf?.nodes?.findIndex((node) => node.id === checkoutNodeId) || -1;
    return currentPaymentWf?.nodes?.length && index >= 0 ? index : -1;
  }

  /** Where the provider sends the buyer back to, carrying the row the payment was for. */
  private successUrl(successWfId: string | undefined, referenceColumn: string): string {
    const refIdParam = regexpGetOnlyLettersAndNumbers(
      this.schemaService.replaceColumnIdsWithNames(referenceColumn),
    );
    return `"${this.apiBaseUrl}/workflows/${successWfId}/checkout/redirect?externalAccountRowId=" + $$Account["_id"] + "&${refIdParam}=" + $$input1["${referenceColumn}"]`;
  }

  /** The account's own domain when it has one, so the buyer returns to the app they came from. */
  private redirectBaseUrl(): string {
    const url = this.runtimeFacade.account?.cName ?? this.apiBaseUrl;
    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
  }
}
